import { get, omit as _omit } from 'lodash-es';
import { useCallback } from 'react';
import Link from 'next/link';
import { useMutation } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import { updateEventStatusMutation, newMemberFragment } from '../../apollo/events';
import { useAppContext } from '../../contexts/app';
import RsvpDropdown from './rsvp-dropdown';
import { eventPath } from '../../utils/slugify';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:first-of-type)': {
      marginLeft: theme.spacing(0.5)
    }
  },
  goingIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5)
  },
  interestedIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5)
  },
  notGoingIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary
  },
  selectedStatus: {
    textDecoration: 'underline'
  },
  button: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  separator: {
    margin: theme.spacing(0, 0.5)
  },
  changeIcon: {
    fontSize: 22,
    lineHeight: '22px',
    color: theme.palette.text.secondary,
    marginLeft: 0
  },
  summary: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize,
  },
  summaryLink: {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

type Props = {
  event?: Event;
  className?: string;
};

const RSVPAttendeesSummary = (props: Props) => {
  const { event } = props;
  const { memberSummary, membership } = event;
  const { classes } = useStyles();
  const { isLoggedIn, login } = useAppContext();

  const [updateEventStatus] = useMutation(updateEventStatusMutation, {
    update: (cache, { data: { updateEventStatus: newMember } }) => {
      // don't add to cache if already there because
      if (membership) {
        return;
      }

      // update event w/ updated membership object
      try {
        cache.writeFragment({
          id: `Event:${event.id}`,
          fragment: newMemberFragment,
          data: {
            id: event.id,
            __typename: 'Event',
            membership: {
              ..._omit(newMember, ['event']),
              __typename: 'EventMember',
            },
          },
        });
      } catch (e) {
        console.log(e);
        // nothing
      }
    }
  });

  const handleRsvpChange = useCallback((newStatus) => {
    if (!isLoggedIn) {
      login(`${eventPath(event.id, event.name)}?rsvp=${newStatus}`);
      return;
    }

    if (!newStatus) {
      return;
    }

    updateEventStatus({
      variables: {
        eventId: event.id,
        status: newStatus,
      }
    })
      .catch(() => {
        alert('Something went wrong. Please try again.');
      });
  }, [event, isLoggedIn, login, updateEventStatus]);

  if (!memberSummary) {
    return null;
  }

  const status = get(membership, 'status');

  return (
    <div>
      <RsvpDropdown
        value={status}
        onChange={handleRsvpChange}
      />
      <div className={classes.summary}>
        <Link
          href={`${eventPath(event.id, event.name)}/attendees`}
          className={classes.summaryLink}
          >
            {memberSummary.GOING.count} Going{' · '}
            {memberSummary.INTERESTED.count} Interested
        </Link>
      </div>
    </div>
  );
};

export default RSVPAttendeesSummary;
